import React, { useState, useEffect } from "react";
import TopupManagementPage from "../../../entities/SuperAdminPage/TopupManagement";
import { topupService } from "../../../api";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../context/AlertContext";
import { useLocation, useNavigate } from "react-router-dom";

// Backend message returned when a pack can't be deleted because customers have
// already bought it. Matched so the UI can offer deactivate instead.
const PURCHASED_DELETE_MESSAGE =
  "Cannot delete this topup pack as it has already been purchased by customers.";

const TopupManagement = () => {
  const [topups, setTopups] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [purchasedPack, setPurchasedPack] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const selectedCountry = String(location?.state?.country || "").trim();

  const normalizeCountry = (country) =>
    String(country || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

  const normalizeListResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.result)) return response.result;
    return [];
  };

  useEffect(() => {
    fetchTopups();
  }, [selectedCountry]);

  const fetchTopups = async () => {
    LoaderHelper.loaderStatus(true);
    setLoadError("");
    try {
      const result = await topupService.list();
      if (result?.success === false) {
        setTopups([]);
        setLoadError(result?.message || "Could not load topup packs.");
        showAlert(result?.message || "Could not load topup packs.", "error");
        LoaderHelper.loaderStatus(false);
        return;
      }

      const records = normalizeListResponse(result);

      // GET /topup returns every pack for every country; scope to the country
      // the Control Centre navigated in with. A pack belongs to a country when
      // it has a pricing row for it that has not been disabled.
      if (!selectedCountry) {
        setTopups(records);
      } else {
        const targetCountry = normalizeCountry(selectedCountry);
        setTopups(
          records.filter((pack) => {
            if (!Array.isArray(pack?.pricing) || !pack.pricing.length) return false;
            return pack.pricing.some(
              (p) =>
                p?.isActive !== false &&
                normalizeCountry(p?.country) === targetCountry
            );
          })
        );
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      setTopups([]);
      setLoadError(error?.message || "Error fetching topup packs");
      showAlert(error?.message || "Error fetching topup packs", "error");
    }
  };

  const handleCreateTopup = () => {
    navigate("/dashboard/topupManagement/createTopup", {
      state: selectedCountry ? { country: selectedCountry } : undefined,
    });
  };

  const handleViewSales = () => {
    navigate("/dashboard/topupManagement/sales", {
      state: selectedCountry ? { country: selectedCountry } : undefined,
    });
  };

  const handleEditTopup = (topupId) => {
    navigate(`/dashboard/topupManagement/edit/${topupId}`, {
      state: selectedCountry ? { country: selectedCountry } : undefined,
    });
  };

  // Dedicated activate/deactivate endpoints — never delete/recreate, and never
  // a PUT with isActive.
  const handleToggleTopupStatus = async (topupId, currentStatus) => {
    const nextStatus = !currentStatus;
    LoaderHelper.loaderStatus(true);
    try {
      const result = nextStatus
        ? await topupService.activate(topupId)
        : await topupService.deactivate(topupId);

      if (result?.success === true) {
        showAlert(
          result?.message ||
            `Topup pack ${nextStatus ? "activated" : "deactivated"} successfully`,
          "success"
        );
        await fetchTopups();
      } else {
        showAlert(
          result?.message ||
            `Failed to ${nextStatus ? "activate" : "deactivate"} topup pack`,
          "error"
        );
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(
        error?.message ||
          `Error ${nextStatus ? "activating" : "deactivating"} topup pack`,
        "error"
      );
    }
  };

  const handleDeleteTopup = async (topupId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await topupService.delete(topupId);

      if (result?.success === true) {
        showAlert(result?.message || "Topup pack deleted successfully", "success");
        await fetchTopups();
        LoaderHelper.loaderStatus(false);
        return;
      }

      // Documented rejection: the pack has purchases against it. Show the
      // backend's message and offer deactivate — never convert silently.
      const message = result?.message || "Failed to delete topup pack";
      if (String(message).trim() === PURCHASED_DELETE_MESSAGE) {
        const pack = topups.find((item) => (item?._id || item?.id) === topupId);
        setPurchasedPack({
          topupId,
          message,
          isActive: pack?.isActive !== false,
        });
        LoaderHelper.loaderStatus(false);
        return;
      }

      showAlert(message, "error");
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error deleting topup pack", "error");
    }
  };

  const handleDeactivateInstead = async () => {
    if (!purchasedPack?.topupId) return;
    const { topupId } = purchasedPack;
    setPurchasedPack(null);
    await handleToggleTopupStatus(topupId, true);
  };

  return (
    <TopupManagementPage
      topups={topups}
      loadError={loadError}
      selectedCountry={selectedCountry}
      purchasedPack={purchasedPack}
      onCreateTopup={handleCreateTopup}
      onViewSales={handleViewSales}
      onEditTopup={handleEditTopup}
      onToggleTopupStatus={handleToggleTopupStatus}
      onDeleteTopup={handleDeleteTopup}
      onDeactivateInstead={handleDeactivateInstead}
      onDismissPurchasedPack={() => setPurchasedPack(null)}
    />
  );
};

export default TopupManagement;
